import { useEffect, useState } from "react";
import { Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const STORAGE_KEY = "ct_push_prompt_dismissed";

/**
 * Browser-native push notifications opt-in.
 *
 * Strategy:
 * - Wait until user has interacted (or 8 seconds), then offer a subtle pill.
 * - On click, requests Notification permission.
 * - When granted, registers a service-worker subscription (if VAPID configured in window.__PUSH_VAPID_KEY__).
 * - Falls back to native browser permission only — subscription is stored server-side via /public/push/subscribe.
 */
export default function PushPrompt() {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission !== "default") return;
    if (localStorage.getItem(STORAGE_KEY)) return;
    // Nu ne suprapunem cu bannerul de cookies: așteptăm decizia pe consimțământ,
    // apoi încă 30s — un singur overlay o dată, nu trei la primul load.
    let t = null;
    const iv = setInterval(() => {
      if (!localStorage.getItem("ct_cookie_consent_v1")) return;
      clearInterval(iv);
      t = setTimeout(() => setVisible(true), 30000);
    }, 2000);
    return () => { clearInterval(iv); if (t) clearTimeout(t); };
  }, []);

  const decline = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
  };

  const accept = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.info("Notificările au fost respinse.");
        decline();
        return;
      }
      let subscription = null;
      try {
        // Cheia publică VAPID vine de la server (self-generată, fără servicii externe)
        const { data: vk } = await api.get("/public/push/vapid-key");
        const keyStr = vk?.key || window.__PUSH_VAPID_KEY__;
        if ("serviceWorker" in navigator && keyStr) {
          const pad = "=".repeat((4 - (keyStr.length % 4)) % 4);
          const raw = atob((keyStr + pad).replace(/-/g, "+").replace(/_/g, "/"));
          const appKey = new Uint8Array([...raw].map((c) => c.charCodeAt(0)));
          const reg = await navigator.serviceWorker.register("/sw.js");
          subscription = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: appKey,
          });
        }
      } catch (e) { /* ignore — VAPID not configured */ }
      await api.post("/public/push/subscribe", {
        endpoint: subscription?.endpoint || `native:${navigator.userAgent.slice(0, 60)}-${Date.now()}`,
        keys: subscription?.toJSON()?.keys || {},
        user_agent: navigator.userAgent,
      });
      toast.success("Notificări activate. Vei primi vești despre live-uri și cursuri.");
      localStorage.setItem(STORAGE_KEY, "1");
      setVisible(false);
    } catch (e) {
      toast.error("Nu am putut activa notificările.");
    } finally {
      setBusy(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      data-testid="push-prompt"
      className="fixed bottom-24 md:bottom-6 left-4 right-4 md:left-auto md:right-24 md:max-w-sm z-30 p-4 rounded-2xl backdrop-blur-xl border flex items-center gap-3"
      style={{ background: "rgba(255,255,255,0.96)", borderColor: "var(--ct-border-gold)" }}
    >
      <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--ct-emerald)", border: "1.5px solid var(--ct-gold)" }}>
        <Bell size={18} style={{ color: "var(--ct-gold)" }} />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium" style={{ color: "var(--ct-text)" }}>Vrei să fii anunțat la live-uri noi?</p>
        <p className="text-xs mt-0.5" style={{ color: "var(--ct-text-muted)" }}>Activează notificările browserului — fără spam.</p>
      </div>
      <div className="flex flex-col gap-1.5 shrink-0">
        <button onClick={accept} disabled={busy} className="btn-primary !py-1.5 !px-3 text-xs whitespace-nowrap" data-testid="push-accept">
          {busy ? "..." : "Activează"}
        </button>
        <button onClick={decline} className="text-xs underline" style={{ color: "var(--ct-text-dim)" }} data-testid="push-decline">
          Nu acum
        </button>
      </div>
    </div>
  );
}
